import { Autocomplete, FormControl, IconButton } from '@mui/joy';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type AccessKeyActionType } from '~/common/enums';
import { type AvailableAccessKeyInstance } from '@/types/trpc/accessKeys';

import { pagination } from '~/common/constants';
import { trpc } from '@/config/trpc';

import useAccessKeyActionAvailableAccessKeysTableConfig from '../hooks/useAccessKeyActionAvailableAccessKeysTableConfig';
import { useAutocompleteOptions } from '@/shared/hooks/autocomplete';
import useToggle from '@/shared/hooks/UseToggle';

import { BuildingIcon, InlineSpinner, QrIcon } from '@/shared/icons/Icons';
import AccessKeyInstanceScanQrCodeModal from '../../details/components/AccessKeyInstanceScanQrCodeModal';
import PaginatedTable from '@/shared/components/2.0/PaginatedTable';

type Props = {
  actionGroupId?: string;
  actionType: AccessKeyActionType;
  selectedAccessKeyInstanceId?: string;
  selectedAccessKeys: AvailableAccessKeyInstance[];
  setInstanceNotFoundAlertDismissed: (value: boolean) => void;
  setLastInstanceResolutionHadNoMatches: (value: boolean) => void;
  setSelectedAccessKeys: React.Dispatch<React.SetStateAction<AvailableAccessKeyInstance[]>>;
};

const AddAccessKeyActionAvailableAccessKeysSection: React.FC<Props> = ({
  actionGroupId,
  actionType,
  selectedAccessKeyInstanceId,
  selectedAccessKeys,
  setInstanceNotFoundAlertDismissed,
  setLastInstanceResolutionHadNoMatches,
  setSelectedAccessKeys,
}) => {
  const { t } = useTranslation();
  const tableConfig = useAccessKeyActionAvailableAccessKeysTableConfig();

  const toggles = {
    scanQrCode: useToggle(),
  };

  const [filters, setFilters] = useState<{
    buildingIds: string[];
    buildingUnitIds: string[];
    searchTerm: string;
    qrCodeHash: string | null;
    selectedAccessKeyInstanceId: string | null;
  }>({
    buildingIds: [],
    buildingUnitIds: [],
    qrCodeHash: null,
    searchTerm: '',
    selectedAccessKeyInstanceId: null,
  });

  const queries = {
    availableAccessKeyInstances: trpc.accessKeys.instances.available.useInfiniteQuery(
      {
        ...filters,
        accessKeyInstanceId:
          selectedAccessKeyInstanceId || filters.selectedAccessKeyInstanceId || undefined,
        actionGroupId,
        actionType,
      },
      {
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialCursor: pagination.queryParam.FIRST_PAGE,
      },
    ),
    buildingOptions: trpc.building.autocomplete.useQuery({ includeInactive: false }),
    unitOptions: trpc.building.unit.autocompleteForBuildings.useQuery(filters.buildingIds, {
      enabled: !!filters.buildingIds.length,
    }),
  };

  const autocompletes = {
    building: useAutocompleteOptions({
      ...queries.buildingOptions,
      callback: (option) =>
        setFilters((prev) => ({ ...prev, buildingIds: option?.map((opt) => opt.value) ?? [] })),
      multiple: true,
      selected: filters.buildingIds,
    }),
    unit: useAutocompleteOptions({
      ...queries.unitOptions,
      callback: (option) =>
        setFilters((prev) => ({ ...prev, buildingUnitIds: option?.map((opt) => opt.value) ?? [] })),
      disabled: !filters.buildingIds.length,
      enforceSingleOptionPrefill: false,
      multiple: true,
      selected: filters.buildingUnitIds,
    }),
  };

  useEffect(() => {
    if (
      (!filters.qrCodeHash && !filters.selectedAccessKeyInstanceId) ||
      queries.availableAccessKeyInstances.isFetching
    )
      return;
    setLastInstanceResolutionHadNoMatches(false);
    setInstanceNotFoundAlertDismissed(false);

    const availableInstances =
      queries.availableAccessKeyInstances.data?.pages
        .flatMap((page) => page.items)
        .filter(
          (item) =>
            !selectedAccessKeys.some((ak) => ak.accessKeyInstanceId === item.accessKeyInstanceId),
        ) ?? [];

    if (availableInstances.length === 0) {
      setLastInstanceResolutionHadNoMatches(true);
      setFilters((prev) => ({
        ...prev,
        qrCodeHash: null,
        selectedAccessKeyInstanceId: null,
      }));
      return;
    }

    setSelectedAccessKeys((prev) => [...prev, ...availableInstances]);
    setFilters((prev) => ({
      ...prev,
      qrCodeHash: null,
      selectedAccessKeyInstanceId: null,
    }));
  }, [
    filters.qrCodeHash,
    filters.selectedAccessKeyInstanceId,
    queries.availableAccessKeyInstances.data?.pages,
    queries.availableAccessKeyInstances.isFetching,
    selectedAccessKeys,
    setInstanceNotFoundAlertDismissed,
    setLastInstanceResolutionHadNoMatches,
    setSelectedAccessKeys,
  ]);

  const handleScanQrCode = (qrCodeHash: string) => {
    const splitQrCodeHash = qrCodeHash.split('_');
    if (splitQrCodeHash.length !== 2) {
      setFilters((prev) => ({ ...prev, qrCodeHash }));
      return;
    }

    const [, accessKeyInstanceId] = splitQrCodeHash;

    setFilters((prev) => ({ ...prev, selectedAccessKeyInstanceId: accessKeyInstanceId }));
  };

  return (
    <>
      <PaginatedTable
        fixedSizeForSearchBar
        config={tableConfig}
        customActions={
          <IconButton onClick={toggles.scanQrCode.show}>
            <QrIcon />
          </IconButton>
        }
        fetchResult={queries.availableAccessKeyInstances}
        filter={
          <div className="flex flex-row gap-4">
            <FormControl className="flex-1">
              <Autocomplete
                {...autocompletes.building}
                getOptionKey={(option) => option.data.id}
                placeholder={t('_accessKeys.columns.building')}
                startDecorator={
                  autocompletes.building.loading ? <InlineSpinner /> : <BuildingIcon />
                }
              />
            </FormControl>
            <FormControl className="flex-1">
              <Autocomplete
                {...autocompletes.unit}
                placeholder={
                  !filters.buildingIds.length
                    ? t('selectBuildingFirstForUnit')
                    : t('_accessKeys.columns.unit')
                }
                startDecorator={
                  autocompletes.unit.loading && filters.buildingIds.length ? (
                    <InlineSpinner />
                  ) : (
                    <BuildingIcon />
                  )
                }
              />
            </FormControl>
          </div>
        }
        offsetY={550}
        selectedItems={selectedAccessKeys}
        setSearchText={(text) => setFilters((prev) => ({ ...prev, searchTerm: text }))}
        setSelected={(row) => {
          setSelectedAccessKeys((prev) => {
            if (
              prev.some((accessKey) => accessKey.accessKeyInstanceId === row.accessKeyInstanceId)
            ) {
              return prev.filter(
                (accessKey) => accessKey.accessKeyInstanceId !== row.accessKeyInstanceId,
              );
            }
            return [...prev, row];
          });
        }}
      />
      {toggles.scanQrCode.value && (
        <AccessKeyInstanceScanQrCodeModal
          callback={handleScanQrCode}
          closeOnScan={false}
          toggle={toggles.scanQrCode}
        />
      )}
    </>
  );
};

export default AddAccessKeyActionAvailableAccessKeysSection;
